<template>
    <div class="card-body">
        <div class="search-block">
            <ButtonDropdown :title="translate('labels.actions')" v-if="$slots.actionDropdownItems" :disabled="!selectedValues.length">
                <slot name="actionDropdownItems" />
            </ButtonDropdown>
            <InputGroup style="width: 250px">
                <template #prepend>
                    <span class="input-group-text"><i class="icon-search4"></i></span>
                </template>
                <Input v-model="search" type="text" :placeholder="translate('labels.search')"/>
            </InputGroup>
            <button type="button" class="btn btn-light legitRipple" @click="fetchRows()" :disabled="fetchingStarted">
                <i class="icon-reload-alt"></i>
            </button>
            <div class="btn-group" v-if="hasFilters">
                <button type="button" class="btn bg-light legitRipple" @click="$emit('click:filters-button')">
                    <i class="icon-filter3"></i>
                    <span v-if="activeFiltersCount > 0"> ({{ activeFiltersCount }})</span>
                </button>
                <button type="button" class="btn btn-icon btn-danger legitRipple" v-if="activeFiltersCount > 0" @click="$emit('click:clear-filters')">
                    <i class="icon-cross2"></i>
                </button>
            </div>
            <button type="button" class="btn btn-light legitRipple" @click="() => $refs.columnsModal.show()" v-if="allowColumnSettings">
                <i class="icon-cog5"></i>
            </button>
        </div>
        <InputGroup style="width: 200px;">
            <template #prepend>
                <span class="input-group-text">{{ translate('components.datatable.show') }}:</span>
            </template>
            <Select v-model="limit" :options="options" optionKey="key" optionValue="key"/>
        </InputGroup>
    </div>
    <Table :columns="showedColumns" :rows="rows" :orders="orders" :has-border="hasBorder">
        <template #table-body>
            <slot name="table-body"></slot>
        </template>
        <template #th-checkbox v-if="hasCheckbox">
            <Checkbox :model-value="selectedValues.length === rows.length && selectedValues.length !== 0" :disabled="disableCheckbox" @update:model-value="toggleAllCheckbox"/>
        </template>
        <template #td-checkbox="{row}" v-if="hasCheckbox">
            <Checkbox :model-value="selectedValues.includes(row[checkboxKey])" :disabled="disableCheckbox" @click="!disableCheckbox && toggleCheckbox(row)"/>
        </template>
        <template v-for="column in showedColumns" v-slot:[`th-${column.key}`]="{column}">
            <template v-if="column.isShowed">
                <FetchTh :dir="this.ordersByColumnName[column.orderColumn ?? column.key]" :column="column" @change:order="setColumnOrder($event, column.order)">
                    <slot :name="`th-${column.key}`" :column="column">{{ column.name }}</slot>
                </FetchTh>
            </template>
        </template>
        <template v-for="column in showedColumns" v-slot:[`td-${column.key}`]="{column, row}">
            <template v-if="column.isShowed">
                <div :style="{float: column.type === 'number' ? 'right' : 'none', 'text-align': column.type === 'number' ? 'right' : 'none'}">
                    <slot :column="column" :row="row" :name="`td-${column.key}`">{{ row[column.key] ?? '' }}</slot>
                </div>
            </template>
        </template>
        <template #tfoot>
            <slot name="tfoot"></slot>
        </template>
    </Table>
    <div class="card-footer d-flex justify-content-between align-items-center">
        <span>{{ translate('labels.showing') }} {{ start }} - {{ end }} {{ translate('labels.of') }} {{ total }} {{ translate('labels.entries') }}</span>
        <ul class="pagination pagination-flat align-self-center">
            <li :class="['page-item', 1 === page ? 'disabled' : '']" @click="changePage(page - 1)">
                <a class="page-link legitRipple"><i class="icon-arrow-left12"></i></a>
            </li>

            <template v-for="pageNumber in pagesToRender" :key="pageNumber">
                <li
                    :class="['page-item', pageNumber === page ? 'active' : '']"
                    @click="changePage(pageNumber)"
                >
                    <a class="page-link legitRipple">{{ pageNumber }}</a>
                </li>
            </template>

            <li :class="['page-item', 1 === pagesNumber ? 'disabled' : '']" @click="changePage(page + 1)">
                <a class="page-link legitRipple"><i class="icon-arrow-right13"></i></a>
            </li>
        </ul>
    </div>
    <Modal ref="columnsModal" :title="this.$t ?  this.$t('labels.column_settings') : translate('labels.column_settings')">
        <div class="modal-body">
            <div class="table-responsive">
                <table class="table table-bordered table-scrollable">
                    <thead>
                        <tr>
                            <th width="60px"><Checkbox :model-value="allColumnsVisible" @update:model-value="toggleAllColumns" /></th>
                            <th>{{ this.$t ?  this.$t('labels.column_name') : translate('labels.column_name') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="column in mappedColumns" :key="column.key">
                            <td><Checkbox v-model="column.isShowed" /></td>
                            <td>{{ column.name }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </Modal>
</template>

<script>

import Table from "./Table.vue";
import InputGroup from "../../../components/new/form/InputGroup.vue";
import Input from "../../../components/new/form/Input.vue";
import Select from "../../../components/new/form/select/Select.vue";
import FetchTh from "./FetchTh.vue";
import Checkbox from "../../../components/new/form/checkbox/AppCheckbox.vue";
import ButtonDropdown from "../../../spa/components/btnDropdown/ButtonDropdown.vue";
import {httpClient} from "../../../services/HttpClient";
import Modal from "../../../components/new/modal/AppModal.vue";
import Button from "../../../components/new/button/AppButton.vue";
export default {
    name: 'TableComponent',
    props: {
        columns: {
            type: Array,
            required: true
        },
        url: {
            type: String,
            required: true
        },
        orders: {
            type: Array,
            default: () => []
        },
        withCredentials: {
            type: Boolean,
            default: false
        },
        hasCheckbox: {
            type: Boolean,
            default: false
        },
        checkboxKey: {
            type: String,
            default: 'uuid'
        },
        disableCheckbox: {
            type: Boolean,
            default: false
        },
        allowColumnSettings: {
            type: Boolean,
            default: false
        },
        hasFilters: {
            type: Boolean,
            default: false
        },
        activeFiltersCount: {
            type: Number,
            default: 0
        },
        hasBorder: {
            type: Boolean,
            default: false
        },
        handle: {
            type: Object,
            default: null
        }
    },
    emits: ['update:orders', 'update:selectedValues', 'click:filters-button', 'click:clear-filters', 'fetch:success'],
    components: {
        InputGroup,
        Input,
        Table,
        Select,
        FetchTh,
        Checkbox,
        ButtonDropdown,
        Modal,
        Button,
    },
    data() {
        return {
            search: '',
            searchDebounceTimer: null,
            rows: [],
            total: 10,
            limit: 10,
            page: 1,
            meta: {},
            options: [
                {key: 10, value: 10},
                {key: 25, value: 25},
                {key: 50, value: 50},
                {key: 100, value: 100},
            ],
            selectedValues: [],
            additionalParams: null,
            mappedColumns: [],
            fetchingStarted: false,
        }
    },
    methods: {
        mapColumns() {
            this.mappedColumns = this.columns.map(col => {
                return {
                    key: col.key ?? '',
                    name: col.name ?? '',
                    width: col.width ?? 'auto',
                    type: col.type ?? 'string',
                    order: col.order ?? true,
                    isShowed: col.isShowed ?? true,
                    ...col
                }
            })
        },
        fetchRows(additionalParams = null) {
            this.fetchingStarted = true;
            this.selectedValues = [];
            this.$emit('update:selectedValues', this.selectedValues);

            let params = {
                page: this.page,
                limit: this.limit,
            }

            if (this.search !== "") {
                params.search = this.search
            }

            if (additionalParams !== null) {
                this.additionalParams = additionalParams
            }

            params = {...params, ...this.additionalParams}

            if (this.orders) {
                params.orders = this.orders
            }

            // Return the promise so callers can use .then/.catch or await it
            return httpClient.get(this.url, params, this.withCredentials).then((response) => {
                this.rows = response.data.data;
                this.total = response.data.meta.total;
                this.limit = response.data.meta.limit;
                this.page = response.data.meta.page;
                this.meta = response.data.meta;

                this.fetchingStarted = false;
                this.$emit('fetch:success', response.data);
                return response;
            });
        },
        changePage(newValue) {
            if (newValue > this.pagesNumber || newValue < 1 || newValue === this.page) {
                return;
            }
            
            this.selectedValues = [];
            this.page = newValue;

            this.fetchRows();
        },
        setColumnOrder(columnName, orderable) {
            if (!orderable) {
                return;
            }
            let newOrders = JSON.parse(JSON.stringify(this.orders)),
                columnOrdering = this.ordersByColumnName[columnName] ?? null;

            if (columnOrdering === 'desc') {
                newOrders = newOrders.map(function (order)
                {
                    if (order.column === columnName) {
                        order.dir = 'asc';
                    }

                    return order;
                })
            } else if (columnOrdering === 'asc') {
                newOrders = newOrders.filter((order) => order.column !== columnName);
            } else {
                newOrders.push({
                    column: columnName,
                    dir : 'desc'
                })
            }

            this.selectedValues = [];

            this.$emit('update:orders', newOrders)
        },
        toggleCheckbox(row) {
            const rowKey = row[this.checkboxKey];
            const index = this.selectedValues.indexOf(rowKey);

            if (index > -1) {
                this.selectedValues.splice(index, 1);
            } else {
                this.selectedValues.push(rowKey);
            }

            this.$emit('update:selectedValues', this.selectedValues);
        },
        toggleAllCheckbox(checked) {
            if (checked) {
                this.selectedValues = this.rows.map(row => row[this.checkboxKey]);
            } else {
                this.selectedValues = [];
            }
            this.$emit('update:selectedValues', this.selectedValues);
        },
        toggleAllColumns(checked) {
            this.mappedColumns.forEach(col => {
                col.isShowed = checked;
            });
        },
    },
    watch: {
        search() {
            this.page = 1;

            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            this.searchDebounceTimer = setTimeout(() => {
                this.fetchRows();
                this.searchDebounceTimer = null;
            }, 500);
        },
        limit() {
            this.fetchRows();
        },
        orders() {
            this.fetchRows();
        },
        columns: {
            handler() {
                this.mapColumns();
            },
            immediate: true,
            deep: true
        }
    },
    computed: {
        showedColumns() {
            return this.mappedColumns.filter(col => col.isShowed);
        },
        pagesNumber() {
            if (this.total <= this.limit) {
                return 1;
            }

            return Math.ceil(this.total / this.limit);
        },
        pagesToRender() {
            const visible = 5;
            let start = this.page;
            let end = this.page + visible - 1;

            if (this.page > this.pagesNumber - visible) {
                start = this.pagesNumber - visible + 1;
                end = this.pagesNumber;
            }

            start = Math.max(start, 1);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            return pages;
        },
        start() {
            if (this.page === 1) {
                return 1;
            }

            return ((this.page - 1) * this.limit) + 1
        },
        end() {
            let end = (this.start + this.limit) - 1;

            return end > this.total ? this.total : end;
        },
        ordersByColumnName() {
            return this.orders.reduce((result, current)=> (result[current.column] = current.dir, result),{});
        },
        allColumnsVisible() {
            return this.mappedColumns.every(col => col.isShowed);
        }
    },
    mounted() {
        this.handle?.connect(this);
    },
    beforeUnmount() {
        this.handle?.disconnect(this);

        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }
    }
}
</script>

<style scoped>
i.icon-search4{
    font-size: 14px;
}
.card-body{
    display: flex;
    justify-content: space-between;
}
.page-item:not(.disabled){
    cursor: pointer;
}
.search-block {
    display: flex;
    gap: 10px;
}
</style>
